import {
  Configuration,
  FreightInvoiceStatus,
  FreightInvoicesApi,
  FPMInvoiceRuleConfigurationsApi,
  FreightSettlementsConfigurationsApi,
  UpdateAmountToleranceLimitDetails,
  UpdateInvoiceRuleConfigurationStatus,
  UpdateDocumentsRequiredDetails,
  UpdateFreightSettlementsConfigurationStatus,
  UpdateAddToDeliveredDashboardActionDetails,
  DocumentMetadata
} from '@shipwell/settlements-sdk';
import {getAccessToken} from 'App/api/utils';

//these are publicly available routes in the settlements service.
const basePath = process.env.SHIPWELL_SETTLEMENTS_API_BASE_PATH;

const freightInvoicesApi = new FreightInvoicesApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

export function updateFreightInvoiceStatus(invoiceId: string, status: FreightInvoiceStatus) {
  return freightInvoicesApi.updateFreightInvoiceStatus(invoiceId, {status});
}

type GetFreightInvoicesParameters = Parameters<FreightInvoicesApi['getFreightInvoices']>;

export function getFreightInvoices({
  include,
  search,
  serviceProviderId,
  bookingPartyTenantId,
  invoiceableId,
  status,
  statusIn,
  invoiceableReferenceNumber,
  invoiceSource,
  serviceProviderName,
  invoiceNumber,
  remitToName,
  page,
  limit,
  options
}: {
  include?: GetFreightInvoicesParameters[0];
  search?: GetFreightInvoicesParameters[1];
  serviceProviderId?: GetFreightInvoicesParameters[2];
  bookingPartyTenantId?: GetFreightInvoicesParameters[3];
  invoiceableId?: GetFreightInvoicesParameters[4];
  status?: GetFreightInvoicesParameters[5];
  statusIn?: GetFreightInvoicesParameters[6];
  invoiceableReferenceNumber?: GetFreightInvoicesParameters[7];
  invoiceSource?: GetFreightInvoicesParameters[8];
  serviceProviderName?: GetFreightInvoicesParameters[9];
  invoiceNumber?: GetFreightInvoicesParameters[10];
  remitToName?: GetFreightInvoicesParameters[11];
  page?: GetFreightInvoicesParameters[12];
  limit?: GetFreightInvoicesParameters[13];
  options?: GetFreightInvoicesParameters[14];
} = {}) {
  return freightInvoicesApi.getFreightInvoices(
    include,
    search,
    serviceProviderId,
    bookingPartyTenantId,
    invoiceableId,
    status,
    statusIn,
    invoiceableReferenceNumber,
    invoiceSource,
    serviceProviderName,
    invoiceNumber,
    remitToName,
    page,
    limit,
    options
  );
}

export function getFreightInvoiceDocuments(invoiceId: string) {
  return freightInvoicesApi.getFreightInvoiceDocuments(invoiceId);
}

const invoiceRuleConfigurationsApi = new FPMInvoiceRuleConfigurationsApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

export function getInvoiceRuleConfigurations() {
  return invoiceRuleConfigurationsApi.getInvoiceRuleConfigurations();
}

export function updateInvoiceRuleConfigurationsActivationStatus(statuses: UpdateInvoiceRuleConfigurationStatus[]) {
  return invoiceRuleConfigurationsApi.updateInvoiceRuleConfigurationsActivationStatus(statuses);
}

export function updateAmountToleranceLimitInvoiceRuleConfiguration(
  ruleConfigurationId: string,
  updateAmountToleranceLimitDetails: UpdateAmountToleranceLimitDetails
) {
  return invoiceRuleConfigurationsApi.updateInvoiceRuleConfiguration(
    ruleConfigurationId,
    updateAmountToleranceLimitDetails
  );
}

export function updateRequiredDocumentInvoiceRuleConfiguration(
  ruleConfigurationId: string,
  documentsRequiredDetails: UpdateDocumentsRequiredDetails
) {
  return invoiceRuleConfigurationsApi.updateInvoiceRuleConfiguration(ruleConfigurationId, documentsRequiredDetails);
}

const freightSettlementsConfigurationsApi = new FreightSettlementsConfigurationsApi(
  new Configuration({
    basePath,
    apiKey: getAccessToken
  })
);

export const getSettlementsConfigurations = ({
  bookingPartyId,
  page,
  limit
}: {
  bookingPartyId?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
} = {}) => {
  return freightSettlementsConfigurationsApi.listFreightConfigurations(bookingPartyId, page, limit);
};

export const bulkUpdateSettlementsConfigurationsStatus = (statuses: UpdateFreightSettlementsConfigurationStatus[]) => {
  return freightSettlementsConfigurationsApi.bulkUpdateFreightConfigurationsStatus(statuses);
};

export const updateAddToDeliveredDashboardConfiguration = (
  freightConfigurationId: string,
  updateAddToDeliveredDashboardActionDetails: UpdateAddToDeliveredDashboardActionDetails
) => {
  return freightSettlementsConfigurationsApi.updateFreightConfiguration(
    freightConfigurationId,
    updateAddToDeliveredDashboardActionDetails
  );
};

type CreateFreightInvoiceDocumentParameters = Parameters<FreightInvoicesApi['createFreightInvoiceDocument']>;
export interface CreateFreightInvoiceDocumentArgs {
  invoiceId: CreateFreightInvoiceDocumentParameters[0];
  documentsMetadata?: CreateFreightInvoiceDocumentParameters[1];
  //the SDK has this as any[], we know that documents is an array of files
  documents?: Array<File>;
  options?: CreateFreightInvoiceDocumentParameters[3];
}
export async function createFreightInvoiceDocument({
  invoiceId,
  documents,
  documentsMetadata,
  options = {}
}: CreateFreightInvoiceDocumentArgs) {
  return freightInvoicesApi.createFreightInvoiceDocument(
    invoiceId,
    //documents get added in the transformRequest transformer below
    undefined,
    //document metadata gets added in the transformRequest transformer below
    undefined,
    {
      transformRequest: [
        () => {
          //this route receive file(s), so the request is structured as formData
          const formData = new FormData();
          documents?.map((document) => formData.append('documents', document));
          if (documentsMetadata?.document_metadata && documentsMetadata.document_metadata.length > 0) {
            formData.append('documents_metadata', JSON.stringify(documentsMetadata));
          }
          return formData;
        }
      ],
      ...options
    }
  );
}

export const updateFreightInvoiceDocument = ({
  invoiceId,
  documentId,
  documentMetadata
}: {
  invoiceId: string;
  documentId: string;
  documentMetadata: DocumentMetadata;
}) => freightInvoicesApi.updateFreightInvoiceDocument(invoiceId, documentId, documentMetadata);

export const updateAutoGenerateInvoiceConfiguration = ({
  id,
  business_days_after_delivery,
  start_date
}: {
  id: string;
  business_days_after_delivery: number;
  start_date: string | undefined;
}) => {
  return freightSettlementsConfigurationsApi.updateFreightConfiguration(id, {
    rule_type: 'AUTO_GENERATE_INVOICE',
    business_days_after_delivery,
    start_date
  });
};

export const updateAutoDisputeInvoiceConfiguration = ({
  id,
  invoice_amount,
  missing_required_document,
  multiple_invoices_per_invoiceable,
  service_provider_name_mismatch,
  shipment_not_delivered
}: {
  id: string;
  invoice_amount: boolean;
  multiple_invoices_per_invoiceable: boolean;
  service_provider_name_mismatch: boolean;
  shipment_not_delivered: boolean;
  missing_required_document: boolean;
}) => {
  return freightSettlementsConfigurationsApi.updateFreightConfiguration(id, {
    rule_type: 'AUTO_DISPUTE_INVOICE',
    invoice_amount,
    missing_required_document,
    multiple_invoices_per_invoiceable,
    service_provider_name_mismatch,
    shipment_not_delivered
  });
};
